import classnames from 'classnames'
import React from 'react'
import { IProps as ICard } from '../../elements/Card'
import CardRenderer from './CardRenderer'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import { LegacyColor, containerMaxWidth, gridGap, mq } from '../../utils'
import { IProps, ICardProps } from './index'
import { CTALink } from '../../elements/CTALink'

import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { CrosslinkCards as CrosslinkCardsDefault } from './component'

const contentAreaGridGap = '3.75rem'

const calculateCardStyles = (
  card: ICardProps,
  length: number,
  rightBorder: boolean | undefined,
): string => {
  const isHidden = (card as ICard).isHiddenOnMobile || null

  return classnames({
    'card-wrapper': true,
    'border-right': rightBorder,
    'card--isHiddenOnMobile': isHidden,
  })
}

export const CrosslinkCards: React.FC<IProps> = (props: IProps) => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <CrosslinkCardsDefault {...props} />
  }
  return <CrosslinkCardsLegacy {...props} />
}

function CrosslinkCardsLegacy({
  layout,
  eyebrow,
  headline,
  subtitle,
  ctaLink,
  cardColor,
  cards,
  footnote,
  alignment,
  rightBorder,
}: IProps) {
  const classes = classnames({
    'crosslink-cards': true,
    [`crosslink-cards--layout-${layout}`]: layout !== '3-3',
    'border-right': rightBorder,
  })

  const headlineClasses = classnames('headline', {
    'top-content': subtitle === undefined && ctaLink === undefined,
  })
  const subtitleClasses = classnames('crosslink-cards__subtitle', {
    'top-content': ctaLink === undefined,
  })

  return (
    <div className={classes}>
      {eyebrow ? (
        <div className="crosslink-cards__eyebrow">
          <Text brand="legacy" size="lg-eyebrow">
            {eyebrow}
          </Text>
        </div>
      ) : null}
      {headline?.trim() && (
        <div className={headlineClasses}>
          <Text brand="legacy" size="xxl+">
            {headline}
          </Text>
        </div>
      )}
      {subtitle ? (
        <div className={subtitleClasses}>
          <Text brand="legacy" size="lg">
            {subtitle}
          </Text>
        </div>
      ) : null}
      {ctaLink && (
        <div className="crosslink-cards__cta-link top-content">
          <CTALink {...ctaLink} />
        </div>
      )}
      <ol className="content-area">
        {cards.map((card, index) => {
          const wrapperClasses = calculateCardStyles(card, cards.length, rightBorder)
          return (
            <li key={index} className={wrapperClasses}>
              <CardRenderer card={card} cardColor={cardColor} alignment={alignment} />
            </li>
          )
        })}
      </ol>
      {footnote && (
        <div className="footnote">
          <RichText document={footnote} />
        </div>
      )}
      <style jsx>
        {`
          .crosslink-cards {
            display: grid;
            grid-row-gap: ${gridGap.mobile};
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
            grid-template-rows: [eyebrow] auto [top] auto [subtitle] auto [link] auto [content] auto [bottom] auto;
            justify-items: ${alignment || 'center'};

            &__eyebrow {
              margin-bottom: 1.125rem;
              grid-row: eyebrow;
              grid-column: content;
            }

            &__subtitle {
              grid-row: subtitle;
              grid-column: content;
              text-align: center;
              padding-top: 0.5rem;
              max-width: 35em;
            }

            &__cta-link {
              margin-top: 1.75rem;
              grid-row: link;
              grid-column: content;
            }
          }

          .headline {
            grid-row: top;
            grid-column: content;
            display: block;
            text-align: center;
            max-width: 1280px;
          }

          .top-content {
            margin-bottom: 60px;
          }

          .content-area {
            grid-row: content;
            grid-column: content;
            grid-gap: ${contentAreaGridGap};
            max-width: ${containerMaxWidth};
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            padding: 0;
          }

          .footnote {
            grid-row: bottom;
            grid-column: content;
            display: block;
            text-align: center;
            margin-top: 36px;
          }

          .card-wrapper {
            justify-content: ${alignment};
            text-align: ${alignment};
            width: 100%;
          }

          .card-wrapper.border-right {
            position: relative;
          }

          .card-wrapper.border-right::after {
            content: '';
            height: 2px;
            background-color: ${LegacyColor.LightGray};
            position: absolute;
            right: 0;
            bottom: calc(-${contentAreaGridGap} / 2);
            left: 0;
          }

          .card-wrapper.card--isHiddenOnMobile::after,
          .card-wrapper.border-right:last-child::after {
            content: none;
          }

          :global(.card-wrapper > .card, .card-wrapper > .cta-link) {
            height: 100%;
          }

          :global(.card-wrapper > .card, .card-wrapper > .cta-button) {
            height: 100%;
          }

          @media (${mq.tablet}) {
            .card-wrapper {
              flex-basis: calc(50% - ${contentAreaGridGap} / 2);
            }

            .card-wrapper.border-right::after {
              width: 2px;
              height: 100%;
              top: 0;
              right: calc(-${contentAreaGridGap} / 2);
              bottom: 0;
              left: auto;
            }

            .crosslink-cards--layout-2-3 {
              .card-wrapper:nth-child(5n + 1),
              .card-wrapper:nth-child(5n + 2),
              .card-wrapper:nth-child(5n + 5) {
                flex-basis: 100%;
              }
            }
          }

          @media (${mq.desktop}) {
            .card-wrapper {
              flex-basis: calc(33.33% - ${contentAreaGridGap} * 2 / 3);
            }

            .crosslink-cards--layout-2-2 {
              .card-wrapper {
                flex-basis: calc(36% - ${contentAreaGridGap} / 2);
              }
            }

            .crosslink-cards--layout-2-3 {
              .card-wrapper:nth-child(5n + 1),
              .card-wrapper:nth-child(5n + 2) {
                flex-basis: calc(50% - ${contentAreaGridGap} / 2);
              }
              .card-wrapper:nth-child(5n + 5) {
                flex-basis: calc(33.33% - ${contentAreaGridGap} * 2 / 3);
              }
            }
          }
        `}
      </style>
    </div>
  )
}
